/**
 * useCharts — saved charts of the user and of other people («Мои карты»).
 *
 *   GET    /api/v1/charts       → { charts, limit }
 *   GET    /api/v1/charts/:id   → chart + positions + conversationsCount
 *   POST   /api/v1/charts       → the created chart (calcStatus PENDING until the worker finishes)
 *   PATCH  /api/v1/charts/:id   → the updated chart
 *   DELETE /api/v1/charts/:id   → { ok, deletedConversations }
 *
 * A chart in PENDING is polled every PENDING_POLL_MS. A hook instance gives up on an id after
 * PENDING_GIVE_UP_MS counted from the first time IT saw that id PENDING; such ids are reported
 * as stalled so the screen can offer «Повторить». Failures throw ApiError.
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../lib/api-client';
import { ApiError, ensureOk } from '../lib/api-error';
import { useAuth } from '../store/auth';
import type { ChartAngles, ChartAspect, ChartHouse } from './useChart';
import type { TimePrecision } from './usePatchBirthData';

export type { TimePrecision };
export type Relation = 'SON' | 'DAUGHTER' | 'MOTHER' | 'FATHER' | 'PARTNER' | 'FRIEND' | 'COLLEAGUE' | 'OTHER';
export type Sex = 'MALE' | 'FEMALE';
export type CalcStatus = 'PENDING' | 'READY' | 'FAILED';

export interface ChartItem {
  id: string;
  kind: 'PERSON' | 'EVENT' | 'COUNTRY';
  isSelf: boolean;
  name: string;
  sex: Sex | null;
  relation: Relation | null;
  /** YYYY-MM-DD */
  date: string;
  timePrecision: TimePrecision;
  timeExact: string | null;
  placeName: string;
  lat: number;
  lng: number;
  tz: string;
  calcStatus: CalcStatus;
  big3: { sun: string; moon: string; asc: string } | null;
}

/** One planet of a chart; `house` is absent for sky/transit positions (server type PlanetPosition). */
export interface ChartPositionPlanet {
  id: string;
  sign: string;
  position: number;
  absolutePosition: number;
  house?: number;
  retrograde: boolean;
}

/** Mirrors the server's ChartPositions (ParsedNatalChart without raw_response). Sign strings are lowercase English. */
export interface ChartPositions {
  sun: string;
  moon: string;
  ascendant: string;
  venus: string;
  mars: string;
  mercury: string;
  jupiter: string;
  saturn: string;
  uranus?: string;
  neptune?: string;
  pluto?: string;
  northNode?: string;
  southNode?: string;
  lilith?: string;
  chiron?: string;
  planets?: ChartPositionPlanet[];
  houses?: ChartHouse[];
  angles?: ChartAngles;
  aspects?: ChartAspect[];
}

export interface ChartDetail extends ChartItem {
  positions: ChartPositions | null;
  conversationsCount: number;
}

export interface ChartCreateBody {
  kind: 'PERSON';
  name: string;
  sex?: Sex;
  relation: Relation;
  date: string;
  timePrecision: TimePrecision;
  timeExact?: string;
  placeName: string;
  lat: number;
  lng: number;
  tz: string;
}
export type ChartPatchBody = Partial<Omit<ChartCreateBody, 'kind' | 'sex' | 'timeExact'>> & {
  sex?: Sex | null;
  timeExact?: string | null;
};

/** Cap on charts of OTHER people (the own chart is not counted). */
export const MAX_CHARTS = 20;
export const PENDING_POLL_MS = 2500;
export const PENDING_GIVE_UP_MS = 5 * 60_000;

/**
 * Remembers when this hook instance first saw each id PENDING. `track` takes the ids that are
 * PENDING right now, forgets the rest (a chart that turned READY/FAILED or vanished) and tells
 * whether some id is still worth polling.
 */
function usePendingTracker(): {
  track: (pendingIds: string[]) => boolean;
  stalled: (pendingIds: string[]) => Set<string>;
  /** Forgets when `id` was first seen PENDING and re-renders, so it is polled for another PENDING_GIVE_UP_MS. */
  restart: (id: string) => void;
  /** Re-renders the hook when the earliest not-yet-stalled id reaches the give-up time. */
  useWakeAtDeadline: (pendingIds: string[]) => void;
} {
  const seen = useRef(new Map<string, number>());
  const [tick, setTick] = useState(0);

  const track = (pendingIds: string[]): boolean => {
    const now = Date.now();
    const live = new Set(pendingIds);
    for (const id of [...seen.current.keys()]) if (!live.has(id)) seen.current.delete(id);
    let polling = false;
    for (const id of pendingIds) {
      if (!seen.current.has(id)) seen.current.set(id, now);
      if (now - (seen.current.get(id) as number) < PENDING_GIVE_UP_MS) polling = true;
    }
    return polling;
  };

  const stalled = (pendingIds: string[]): Set<string> => {
    track(pendingIds);
    const now = Date.now();
    return new Set(pendingIds.filter((id) => now - (seen.current.get(id) as number) >= PENDING_GIVE_UP_MS));
  };

  const restart = (id: string): void => {
    seen.current.delete(id);
    setTick((n) => n + 1);
  };

  const useWakeAtDeadline = (pendingIds: string[]): void => {
    const key = pendingIds.join('|');
    useEffect(() => {
      const now = Date.now();
      let next = Infinity;
      for (const id of pendingIds) {
        const at = (seen.current.get(id) ?? now) + PENDING_GIVE_UP_MS;
        if (at > now && at < next) next = at;
      }
      if (next === Infinity) return undefined;
      const timer = setTimeout(() => setTick((n) => n + 1), next - now + 1);
      return () => clearTimeout(timer);
    }, [key, tick]);
  };

  return { track, stalled, restart, useWakeAtDeadline };
}

const pendingIdsOf = (charts: ChartItem[] | undefined): string[] =>
  (charts ?? []).filter((c) => c.calcStatus === 'PENDING').map((c) => c.id);

const JSON_HEADERS = { 'content-type': 'application/json' };

/**
 * Drops the cached chat pieces that show this chart's name/relation: its openers
 * (`['chat-opener', id, …]`) and saved threads about it (`['chat-thread', threadId]` whose data has
 * this chartId). Both are cached with staleTime Infinity, so a renamed or deleted chart would
 * otherwise keep its old header.
 */
function forgetChatCaches(qc: ReturnType<typeof useQueryClient>, id: string): void {
  qc.removeQueries({ predicate: (q) => q.queryKey[0] === 'chat-opener' && q.queryKey[1] === id });
  qc.removeQueries({
    predicate: (q) =>
      q.queryKey[0] === 'chat-thread' && (q.state.data as { chartId?: string | null } | undefined)?.chartId === id,
  });
}

export function useCharts(): {
  charts: ChartItem[];
  limit: number;
  othersCount: number;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  stalledIds: Set<string>;
  /** After a retry of a stalled chart: clears its stalled state, refetches now and polls again. */
  restartPolling: (id: string) => void;
} {
  const jwt = useAuth((s) => s.jwt);
  const tracker = usePendingTracker();

  const q = useQuery<{ charts: ChartItem[]; limit: number }>({
    queryKey: ['charts'],
    enabled: !!jwt,
    staleTime: 30_000,
    queryFn: async () => {
      const res = await ensureOk(await apiFetch('/api/v1/charts', jwt as string));
      return (await res.json()) as { charts: ChartItem[]; limit: number };
    },
    refetchInterval: (query) => (tracker.track(pendingIdsOf(query.state.data?.charts)) ? PENDING_POLL_MS : false),
  });

  const charts = q.data?.charts ?? [];
  const pendingIds = pendingIdsOf(charts);
  const stalledIds = tracker.stalled(pendingIds);
  tracker.useWakeAtDeadline(pendingIds);

  return {
    charts,
    limit: q.data?.limit ?? MAX_CHARTS,
    othersCount: charts.filter((c) => !c.isSelf).length,
    isLoading: q.isLoading,
    isError: q.isError,
    refetch: () => void q.refetch(),
    stalledIds,
    restartPolling: (chartId) => {
      tracker.restart(chartId);
      void q.refetch();
    },
  };
}

export function useChartDetail(id: string | undefined): {
  chart: ChartDetail | undefined;
  isLoading: boolean;
  isError: boolean;
  notFound: boolean;
  stalled: boolean;
  refetch: () => void;
  /** After a retry of a stalled chart: clears `stalled`, refetches now and polls again. */
  restartPolling: () => void;
} {
  const jwt = useAuth((s) => s.jwt);
  const tracker = usePendingTracker();

  const q = useQuery<ChartDetail, Error>({
    queryKey: ['chart', id],
    enabled: !!jwt && !!id,
    staleTime: 30_000,
    retry: (failureCount, error) => !(error instanceof ApiError && error.status === 404) && failureCount < 3,
    queryFn: async () => {
      const res = await ensureOk(await apiFetch(`/api/v1/charts/${encodeURIComponent(id as string)}`, jwt as string));
      return (await res.json()) as ChartDetail;
    },
    refetchInterval: (query) => {
      const d = query.state.data;
      return tracker.track(d?.calcStatus === 'PENDING' ? [d.id] : []) ? PENDING_POLL_MS : false;
    },
  });

  const pendingIds = q.data?.calcStatus === 'PENDING' ? [q.data.id] : [];
  const stalled = tracker.stalled(pendingIds).size > 0;
  tracker.useWakeAtDeadline(pendingIds);

  return {
    chart: q.data,
    isLoading: q.isLoading,
    isError: q.isError,
    notFound: q.error instanceof ApiError && q.error.status === 404,
    stalled,
    refetch: () => void q.refetch(),
    restartPolling: () => {
      if (id) tracker.restart(id);
      void q.refetch();
    },
  };
}

export function useCreateChart(): { mutateAsync: (b: ChartCreateBody) => Promise<ChartItem>; isPending: boolean } {
  const jwt = useAuth((s) => s.jwt);
  const qc = useQueryClient();
  const m = useMutation<ChartItem, Error, ChartCreateBody>({
    mutationFn: async (body) => {
      if (!jwt) throw new Error('not_authenticated');
      const res = await ensureOk(
        await apiFetch('/api/v1/charts', jwt, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) }),
      );
      return (await res.json()) as ChartItem;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['charts'] });
    },
  });
  return { mutateAsync: m.mutateAsync, isPending: m.isPending };
}

export function useUpdateChart(id: string): { mutateAsync: (b: ChartPatchBody) => Promise<ChartItem>; isPending: boolean } {
  const jwt = useAuth((s) => s.jwt);
  const qc = useQueryClient();
  const m = useMutation<ChartItem, Error, ChartPatchBody>({
    mutationFn: async (body) => {
      if (!jwt) throw new Error('not_authenticated');
      const res = await ensureOk(
        await apiFetch(`/api/v1/charts/${encodeURIComponent(id)}`, jwt, {
          method: 'PATCH',
          headers: JSON_HEADERS,
          body: JSON.stringify(body),
        }),
      );
      return (await res.json()) as ChartItem;
    },
    onSuccess: () => {
      forgetChatCaches(qc, id);
      void qc.invalidateQueries({ queryKey: ['charts'] });
      void qc.invalidateQueries({ queryKey: ['chart', id] });
    },
  });
  return { mutateAsync: m.mutateAsync, isPending: m.isPending };
}

export function useDeleteChart(): {
  mutateAsync: (id: string) => Promise<{ deletedConversations: number }>;
  isPending: boolean;
} {
  const jwt = useAuth((s) => s.jwt);
  const qc = useQueryClient();
  const m = useMutation<{ deletedConversations: number }, Error, string>({
    mutationFn: async (id) => {
      if (!jwt) throw new Error('not_authenticated');
      const res = await ensureOk(await apiFetch(`/api/v1/charts/${encodeURIComponent(id)}`, jwt, { method: 'DELETE' }));
      const out = (await res.json()) as { deletedConversations?: number };
      return { deletedConversations: out.deletedConversations ?? 0 };
    },
    onSuccess: (_out, id) => {
      qc.removeQueries({ queryKey: ['chart', id] });
      forgetChatCaches(qc, id);
      // Drop this chart's thread list first so the invalidation below does not refetch it into a 404.
      qc.removeQueries({ queryKey: ['chat-threads', id] });
      void qc.invalidateQueries({ queryKey: ['charts'] });
      void qc.invalidateQueries({ queryKey: ['chat-threads'] });
    },
  });
  return { mutateAsync: m.mutateAsync, isPending: m.isPending };
}
